<template>
  <section class="polo-area">
    <WindowModal :title="modalTitle" type="Global">
      <template #modal-content>
        <BuyTickets
          ref="buyTickets"
          :lottery="lottery"
          :sold-tickets="tickets"
          :transaction="transaction"
          @buyTickets="buyTickets"
          @initConfirmState="changeModalTitle('init')"
          @initLoading="changeModalTitle('loading')"
          @initSuccess="changeModalTitle('success')"
          @initError="changeModalTitle('error')"
          @closeGlobalModal="closeModal"
        />
      </template>
    </WindowModal>
    <WindowModal :title="listTitle" type="List">
      <template #modal-content>
        <TicketsList
          ref="ticketsList"
          :tickets="tickets"
          @closeListModal="closeListModal"
        />
      </template>
    </WindowModal>
    <div class="polo-header">
      <picture>
        <source srcset="/img/new/water_polo.webp" media="(min-width: 1024px)" type="image/webp">
        <source srcset="/img/new/water_polo.png" media="(min-width: 1024px)">
        <source srcset="/img/new/water_polo_mobile.webp" type="image/webp">
        <nuxt-img
          fit="inside"
          width="1600"
          sizes="sm:650px md:800px lg:1200px xl:1400px xxl:1600px"
          src="/img/new/water_polo_mobile.png"
          alt="Earn $FLOATIE and fiat on Defi Pool Finance"
          title="$FLOATIE lottery"
        />
      </picture>
      <div class="texts">
        <h1>
          {{ $t('lottery.water_polo') }}
        </h1>
        <div class="prize-pool">
          <div v-if="!lotteryLoading" class="h2">
            $ {{ $priceFormat(floatieToUsd(lottery.amountCollectedInFloatie), 2) }}
          </div>
          <skeleton v-else width="300px" height="60px" class="h2" />
          <div class="h6">
            {{ $t('lottery.in_prizes') }}
          </div>
        </div>
        <p class="desc">
          {{ $t('lottery.guess_desc') }}
        </p>
        <div v-if="lotteryActive" class="buttons-area">
          <button v-if="user !== null" type="button" class="btn pink" @click="openBuyModal">
            {{ $t('lottery.buy_tickets_cta') }}
          </button>
          <button v-else type="button" class="btn pink" @click="openConnectModal">
            {{ $t('common.connect_wallet') }}
          </button>
        </div>
        <div v-else class="buttons-area">
          <button type="button" class="btn pink" @click="scrollToResults">
            {{ $t('lottery.see_recent') }}
          </button>
        </div>
      </div>
    </div>
    <div ref="resultsSection" class="lottery-area">
      <div class="left">
        <div class="heading">
          <div v-if="lotteryActive" class="h3">
            {{ $t('lottery.buy_tickets') }}
          </div>
          <div v-else class="h3">
            {{ $t('lottery.buy_tickets_later') }}
          </div>
          <div v-if="lotteryActive" class="timer">
            <div class="number days">
              <p v-if="!lotteryLoading" class="text">
                {{ drawTimeLeft('days') }}
              </p>
              <Skeleton v-else width="53px" height="38px" class="text" />
              <p class="desc">
                {{ $t('common.days') }}
              </p>
            </div>
            <div class="separate">
              :
            </div>
            <div class="number hours">
              <p v-if="!lotteryLoading" class="text">
                {{ drawTimeLeft('hours') }}
              </p>
              <Skeleton v-else width="53px" height="38px" class="text" />
              <p class="desc">
                {{ $t('common.hours') }}
              </p>
            </div>
            <div class="separate">
              :
            </div>
            <div class="number minutes">
              <p v-if="!lotteryLoading" class="text">
                {{ drawTimeLeft('minutes') }}
              </p>
              <Skeleton v-else width="53px" height="38px" class="text" />
              <p class="desc">
                {{ $t('common.minutes') }}
              </p>
            </div>
          </div>
          <div v-else class="timer">
            <div class="header">
              {{ $t('lottery.calculating_results') }}...
            </div>
          </div>
        </div>
        <div class="accordion-box">
          <div class="header">
            <div v-if="lotteryActive" class="h4">
              {{ $t('lottery.next_draw') }}
            </div>
            <div v-else class="h4">
              {{ $t('lottery.current_draw') }}
            </div>
            <div class="details">
              <p v-if="!lotteryLoading">
                # {{ lottery.id }}
              </p>
              <Skeleton v-else width="40px" height="14px" />
              <p v-if="lotteryActive">
                &nbsp;|&nbsp;
              </p>
              <p v-if="lotteryActive">
                {{ $t('lottery.draw') }}:&nbsp;
              </p>
              <p v-if="!lotteryLoading && lotteryActive">
                {{ $stringifyDate(lottery.endTime, true) }}
              </p>
              <Skeleton v-else-if="lotteryActive" width="175px" height="14px" />
            </div>
          </div>
          <hr>
          <div class="prize-pot">
            <div v-if="lotteryActive" class="number-area">
              <div class="h6">
                {{ $t('lottery.prize_pot') }}
              </div>
              <div v-if="!lotteryLoading" class="usd">
                ~${{ $priceFormat(floatieToUsd(lottery.amountCollectedInFloatie), 2) }}
              </div>
              <Skeleton v-else width="180px" height="39px" class="usd" />
              <div class="crypto">
                <div class="floatie">
                  <FloatieLogo />
                </div>
                <p v-if="!lotteryLoading" class="number">
                  {{ lottery.amountCollectedInFloatie }} {{ floatie.symbol.toUpperCase() }}
                </p>
                <Skeleton v-else width="80px" height="14px" class="number" />
              </div>
            </div>
            <div v-else class="number-area">
              <div class="h6">
                {{ $t('lottery.prize_pot') }}
              </div>
              <div class="usd">
                {{ $t('lottery.calculating') }}...
              </div>
            </div>
            <div class="tickets-area">
              <div v-if="tickets.length > 0 && user !== null" class="h6 cursor-pointer" @click="openListModal">
                {{ $t('lottery.show_tickets', {
                  'tickets': tickets.length,
                  'tickets_text': $numberToString(tickets.length, [
                    $t('lottery.ticket'),
                    $t('lottery.tickets'),
                    $t('lottery.many_tickets')
                  ]),
                }) }}
              </div>
              <div v-else class="h6">
                {{ $t('lottery.your_tickets') }}
              </div>
              <div v-if="lotteryActive" class="buttons-area">
                <button v-if="user !== null" type="button" class="btn pink" @click="openBuyModal">
                  {{ $t('lottery.buy_tickets_cta') }}
                </button>
                <button v-else type="button" class="btn pink" @click="openConnectModal">
                  {{ $t('common.connect_wallet') }}
                </button>
              </div>
              <div v-else class="buttons-area">
                <button type="button" class="btn pink disabled" disabled>
                  {{ $t('lottery.on_sale_soon') }}
                </button>
              </div>
            </div>
          </div>
          <hr class="reverse">
          <div ref="accordion1" :class="{'accordion': true, 'active': accordions.accordion1}">
            <div ref="accordionArea1" class="accordion-area">
              <p class="desc">
                {{ $t('lottery.match_text') }}
              </p>
              <div class="match-list">
                <div v-for="index in 6" :key="index" class="match">
                  <div class="h6">
                    {{ (index + 1 !== 6 ? $t('lottery.match_first') : $t('lottery.match_all')) + ' ' + index }}
                  </div>
                  <div v-if="!lotteryLoading" class="h4">
                    {{ grabAmount('current', index) + ' ' + floatie.symbol.toUpperCase() }}
                  </div>
                  <Skeleton v-else width="100px" height="20px" class="h4" />
                  <p v-if="!lotteryLoading" class="usd">
                    ~${{ $priceFormat(floatieToUsd(grabAmount('current', index)), 2) }}
                  </p>
                  <Skeleton v-else width="70px" height="14px" class="usd" />
                </div>
                <div class="match burned">
                  <div class="h6">
                    {{ $t('lottery.burn') }}
                  </div>
                  <div v-if="!lotteryLoading" class="h4">
                    {{ getBurnAmount('current') + ' ' + floatie.symbol.toUpperCase() }}
                  </div>
                  <Skeleton v-else width="100px" height="20px" class="h4" />
                  <p v-if="!lotteryLoading" class="usd">
                    ~${{ $priceFormat(floatieToUsd(getBurnAmount('current')), 2) }}
                  </p>
                  <Skeleton v-else width="70px" height="14px" class="usd" />
                </div>
              </div>
            </div>
            <div ref="accordionHeader1" class="accordion-header" @click="openAccordion(1)">
              <p v-if="accordions.accordion1">
                {{ $t('common.hide') }}
              </p>
              <p v-else>
                {{ $t('lottery.details') }}
              </p>
              <ChevronDown />
            </div>
          </div>
        </div>
      </div>
      <div class="right">
        <div class="heading">
          <div class="h3">
            {{ $t('lottery.finished') }}
          </div>
          <div class="range-switch-area">
            <div :class="{'switch-item': true, 'active': history.filter === 'all'}" @click="changeHistoryTab('all')">
              <span>
                {{ $t('lottery.all_history') }}
              </span>
            </div>
            <div :class="{'switch-item': true, 'active': history.filter === 'user'}" @click="changeHistoryTab('user')">
              <span>
                {{ $t('lottery.your_history') }}
              </span>
            </div>
          </div>
        </div>
        <div class="accordion-box">
          <div class="header">
            <div v-if="!lotteryPrevLoading" class="h4">
              {{ $t('lottery.round') + ' #' + lotteryPrev.id }}
            </div>
            <Skeleton v-else width="100px" height="16px" class="h4" />
            <div class="details">
              <p>
                {{ $t('lottery.drawn') }}:&nbsp;
              </p>
              <p v-if="!lotteryPrevLoading">
                {{ $stringifyDate(lotteryPrev.endTime, true) }}
              </p>
              <Skeleton v-else width="165px" height="14px" />
            </div>
          </div>
          <hr>
          <div class="winning-number">
            <div class="h6">
              {{ $t('lottery.winning_number') }}
              <span class="color">
                {{ $t('lottery.latest') }}
              </span>
            </div>
            <div v-if="!lotteryPrevLoading" class="numbers">
              <div v-for="index in 6" :key="index" :class="['number', 'color' + index]">
                <p>
                  {{ wonNumber(index) }}
                </p>
              </div>
            </div>
            <div v-else class="numbers">
              <div v-for="index in 6" :key="index" :class="['number', 'color' + index]">
                <Skeleton width="20px" height="30px" />
              </div>
            </div>
          </div>
          <hr class="reverse">
          <div ref="accordion2" :class="{'accordion': true, 'active': accordions.accordion2}">
            <div ref="accordionArea2" class="accordion-area">
              <div class="pot-results">
                <div class="totals">
                  <div class="h6">
                    {{ $t('lottery.prize_pot') }}
                  </div>
                  <div v-if="!lotteryPrevLoading" class="usd">
                    ~${{ $priceFormat(floatieToUsd(lotteryPrev.amountCollectedInFloatie), 2) }}
                  </div>
                  <Skeleton v-else width="145px" height="31px" class="usd" />
                  <div class="crypto">
                    <div class="floatie">
                      <FloatieLogo />
                    </div>
                    <p v-if="!lotteryPrevLoading" class="number">
                      {{ lotteryPrev.amountCollectedInFloatie }} {{ floatie.symbol.toUpperCase() }}
                    </p>
                    <Skeleton v-else width="80px" height="14px" class="number" />
                  </div>
                  <div class="h6">
                    {{ $t('lottery.total_players') }}
                  </div>
                  <p v-if="!lotteryPrevLoading" class="number">
                    {{ getTotalPlayers }}
                  </p>
                  <Skeleton v-else width="23px" height="14px" class="number" />
                </div>
                <div class="grabs">
                  <p class="desc">
                    {{ $t('lottery.match_text') }}
                  </p>
                  <div class="match-list">
                    <div v-for="index in 6" :key="index" class="match">
                      <div class="h6">
                        {{ (index + 1 !== 6 ? $t('lottery.match_first') : $t('lottery.match_all')) + ' ' + index }}
                      </div>
                      <div v-if="!lotteryPrevLoading" class="h4">
                        {{ grabAmount('old', index) + ' ' + floatie.symbol.toUpperCase() }}
                      </div>
                      <Skeleton v-else width="100px" height="20px" class="h4" />
                      <p v-if="!lotteryPrevLoading" class="usd">
                        ~${{ $priceFormat(floatieToUsd(grabAmount('old', index)), 2) }}
                      </p>
                      <Skeleton v-else width="70px" height="14px" class="usd" />
                    </div>
                    <div class="match burned">
                      <div class="h6">
                        {{ $t('lottery.burn') }}
                      </div>
                      <div v-if="!lotteryPrevLoading" class="h4">
                        {{ getBurnAmount('old') + ' ' + floatie.symbol.toUpperCase() }}
                      </div>
                      <Skeleton v-else width="100px" height="20px" class="h4" />
                      <p v-if="!lotteryPrevLoading" class="usd">
                        ~${{ $priceFormat(floatieToUsd(getBurnAmount('old')), 2) }}
                      </p>
                      <Skeleton v-else width="70px" height="14px" class="usd" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div ref="accordionHeader2" class="accordion-header" @click="openAccordion(2)">
              <p v-if="accordions.accordion2">
                {{ $t('common.hide') }}
              </p>
              <p v-else>
                {{ $t('lottery.details') }}
              </p>
              <ChevronDown />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="user === null" class="win-area">
      <picture>
        <source srcset="/img/new/connect_wallet.webp" type="image/webp">
        <nuxt-img
          fit="inside"
          width="2188"
          sizes="sm:250px md:300px lg:400px xl:500px xxl:600px"
          src="/img/new/connect_wallet.png"
          alt="Connect wallet to earn prize on Defi Pool Finance"
          title="Connect wallet"
          loading="lazy"
        />
      </picture>
      <div class="h2">
        {{ $t('lottery.connect_wallet') }}
      </div>
      <div class="buttons-area">
        <button type="button" class="btn cyan" @click="openConnectModal">
          {{ $t('common.connect_wallet') }}
        </button>
      </div>
    </div>
    <div class="howto-area">
      <div class="h3">
        {{ $t('lottery.hot_to_play_title') }}
      </div>
      <p class="desc">
        {{ $t('lottery.hot_to_play_desc') }}
      </p>
      <div class="items">
        <div class="item">
          <picture>
            <source srcset="/img/new/tickets.webp" type="image/webp">
            <nuxt-img
              fit="inside"
              width="3851"
              sizes="sm:250px md:275px lg:300px xl:400px xxl:500px"
              src="/img/new/tickets.png"
              alt="Buy lottery tickets to earn and win on Defi Pool Finance"
              title="Buy lottery tickets"
              loading="lazy"
            />
          </picture>
          <div class="text-area">
            <div class="h6">
              {{ $t('lottery.buy_tickets_header') }}
            </div>
            <p class="desc">
              {{ $t('lottery.buy_tickets_desc') }}
            </p>
          </div>
        </div>
        <div class="item">
          <picture>
            <source srcset="/img/new/calendar.webp" type="image/webp">
            <nuxt-img
              fit="inside"
              width="2113"
              sizes="sm:250px md:275px lg:300px xl:400px xxl:500px"
              src="/img/new/calendar.png"
              alt="Plan your crypto income with Defi Pool Finance"
              title="Wait the draw"
              loading="lazy"
            />
          </picture>
          <div class="text-area">
            <div class="h6">
              {{ $t('lottery.wait_draw_header') }}
            </div>
            <p class="desc">
              {{ $t('lottery.wait_draw_desc') }}
            </p>
          </div>
        </div>
        <div class="item">
          <picture>
            <source srcset="/img/new/prize.webp" type="image/webp">
            <nuxt-img
              fit="inside"
              width="1464"
              sizes="sm:250px md:275px lg:300px xl:400px xxl:500px"
              src="/img/new/prize.png"
              alt="Win biggest crypto prize on Defi Pool Finance"
              title="Win prize"
              loading="lazy"
            />
          </picture>
          <div class="text-area">
            <div class="h6">
              {{ $t('lottery.check_prizes_header') }}
            </div>
            <p class="desc">
              {{ $t('lottery.check_prizes_desc') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="winning-area">
      <div class="text-area">
        <div class="h3">
          {{ $t('lottery.criteria_winning') }}
        </div>
        <p class="desc">
          {{ $t('lottery.criteria_winning_p1') }}
        </p>
        <div class="icon-text">
          <CheckFilled />
          <p class="desc">
            {{ $t('lottery.criteria_winning_p2') }}
          </p>
        </div>
        <div class="icon-text">
          <CheckFilled />
          <p class="desc">
            {{ $t('lottery.criteria_winning_p3') }}
          </p>
        </div>
        <p class="desc">
          {{ $t('lottery.criteria_winning_p4') }}
        </p>
      </div>
      <div class="schema-area">
        <div class="numbers">
          <div class="item-area"></div>
          <div v-for="(number, index) in exampleNumbers.lottery.split('')" :key="index" class="item-area">
            <div :class="['number', 'color' + (index + 1)]">
              <p>
                {{ number }}
              </p>
            </div>
          </div>
        </div>
        <div class="icons-row">
          <div class="item-area"></div>
          <div v-for="index in 6" :key="index" class="item-area">
            <div v-if="index < 5 || index > 5" class="check">
              <CheckOutline />
            </div>
            <div v-else class="cross">
              <Plus />
            </div>
          </div>
        </div>
        <div class="tickets-row mr-4">
          <div class="item-area">
            A
          </div>
          <div v-for="(number, index) in exampleNumbers.ticketA.split('')" :key="index" :class="{'item-area': true, 'bordered': index < 4, 'disabled': index > 3, 'first': index === 0, 'last': index === 3}">
            <div class="ticket-number">
              <p>
                {{ number }}
              </p>
            </div>
          </div>
        </div>
        <div class="icons-row">
          <div class="item-area"></div>
          <div v-for="index in 6" :key="index" class="item-area">
            <div v-if="index !== 1" class="check">
              <CheckOutline />
            </div>
            <div v-else class="cross">
              <Plus />
            </div>
          </div>
        </div>
        <div class="tickets-row">
          <div class="item-area">
            B
          </div>
          <div v-for="(number, index) in exampleNumbers.ticketB.split('')" :key="index" class="item-area disabled">
            <div class="ticket-number disabled">
              <p>
                {{ number }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr class="divider">
    <div class="funds-area">
      <div class="text-area">
        <div class="h3">
          {{ $t('lottery.prize_fund_header') }}
        </div>
        <p class="desc">
          {{ $t('lottery.prize_fund_desc') }}
        </p>
        <div class="h6">
          {{ $t('lottery.ticket_purchases_header') }}
        </div>
        <div class="icon-text">
          <CheckFilled />
          <p class="desc">
            {{ $t('lottery.ticket_purchases_desc') }}
          </p>
        </div>
        <div class="h6">
          {{ $t('lottery.rollover_prizes_header') }}
        </div>
        <div class="icon-text">
          <CheckFilled />
          <p class="desc">
            {{ $t('lottery.rollover_prizes_desc') }}
          </p>
        </div>
        <div class="h6">
          {{ $t('lottery.injections_header') }}
        </div>
        <div class="icon-text">
          <CheckFilled />
          <p class="desc">
            {{ $t('lottery.injections_desc') }}
          </p>
        </div>
      </div>
      <div class="table-area">
        <div class="matches">
          <div class="h6">
            {{ $t('lottery.digits_matched') }}
          </div>
          <div v-for="index in 6" :key="index" class="match">
            <div :class="['number', 'color' + index]">
            </div>
            <p class="desc">
              {{ (index !== 6 ? $t('lottery.match_first') : $t('lottery.match_all')) + ' ' + index }}
            </p>
          </div>
        </div>
        <div v-if="!lotteryLoading" class="percent">
          <div class="h6">
            {{ $t('lottery.prize_pool_allocation') }}
          </div>
          <div v-for="index in 6" :key="index" class="value">
            {{ $priceFormat(parseFloat(lottery.rewardsBreakdown[index - 1]) / 100, 1) }}%
          </div>
        </div>
        <div v-else class="percent">
          <div class="h6">
            {{ $t('lottery.prize_pool_allocation') }}
          </div>
          <Skeleton v-for="index in 6" :key="index" width="75px" height="21px" class="value" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  hydrateWhenIdle,
  hydrateWhenVisible
} from 'vue-lazy-hydration'

import { Skeleton } from 'vue-loading-skeleton'

import BuyTickets from '~/components/app/interface/BuyTickets'
import TicketsList from '~/components/app/interface/TicketsList'

export default {
  name: 'LotteryPage',
  components: {
    BuyTickets,
    CheckFilled: hydrateWhenIdle(() => import('~/components/svg/new/CheckFilled')),
    CheckOutline: hydrateWhenIdle(() => import('~/components/svg/new/CheckOutline')),
    ChevronDown: hydrateWhenIdle(() => import('~/components/svg/new/ChevronDown')),
    FloatieLogo: hydrateWhenVisible(() => import('~/components/svg/new/FloatieLogo')),
    Plus: hydrateWhenVisible(() => import('~/components/svg/new/Plus')),
    Skeleton,
    TicketsList,
    WindowModal: hydrateWhenIdle(() => import('~/components/modals/WindowModal'))
  },
  layout: 'new',
  data() {
    const nowDate = Date.now()
    return {
      modalTitle: this.$t('lottery.buy_tickets_cta'),
      listTitle: this.$t('lottery.round'),
      accordions: {
        accordion1: false,
        accordion2: false
      },
      exampleNumbers: {
        lottery: '729315',
        ticketA: '729385',
        ticketB: '629315'
      },
      floatie: {
        name: 'Floatie',
        symbol: 'FLT',
        decimals: 14,
        eth_price: 0.0004,
        usd_price: 1.64
      },
      filter: {
        page: 1,
        step: 20,
        orderField: 'created_at_desc',
        lid: 0,
        uid: 0
      },
      matchingBreakdown: [2, 3, 5, 10, 20, 40],
      lottery: {
        id: 1,
        status: 1,
        startTime: nowDate - 250000,
        endTime: nowDate,
        priceTicketInFloatie: 1,
        discountDivisor: 2000,
        rewardsBreakdown: [250, 375, 625, 1250, 2500, 5000],
        treasuryFee: 2000,
        floatiePerBracket: [0, 0, 0, 0, 0, 0],
        countWinnersPerBracket: [0, 0, 0, 0, 0, 0],
        firstTicketId: 1,
        firstTicketIdNextLottery: 2,
        amountCollectedInFloatie: 0,
        finalNumber: 1672428
      },
      lotteryPrev: {
        id: 1,
        status: 1,
        startTime: nowDate - 250000,
        endTime: nowDate,
        priceTicketInFloatie: 1,
        discountDivisor: 2000,
        rewardsBreakdown: [250, 375, 625, 1250, 2500, 5000],
        treasuryFee: 2000,
        floatiePerBracket: [0, 0, 0, 0, 0, 0],
        countWinnersPerBracket: [0, 0, 0, 0, 0, 0],
        firstTicketId: 1,
        firstTicketIdNextLottery: 2,
        amountCollectedInFloatie: 0,
        finalNumber: 1672428
      },
      transaction: {
        gasFee: 3000000,
        tx: '',
        hash: '',
        loading: false,
        pending: false
      },
      lotteryLoading: true,
      lotteryPrevLoading: true,
      userPrevTickets: [],
      history: {
        page: 1,
        step: 20,
        lid: null,
        uid: null,
        filter: 'all'
      }
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['darkTheme']),
    ...mapGetters('auth', [
      'user'
    ]),
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    ...mapGetters('lottery', [
      'lotteries',
      'total',
      'lotteriesLoading',
      'tickets',
      'ticketsLoading'
    ]),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    },
    getTotalPlayers() {
      if (this.lotteryPrev.countWinnersPerBracket === null) {
        return 0
      }
      let players = 0
      for (let ind = 0; ind < 6; ++ind) {
        players += parseInt(this.lotteryPrev.countWinnersPerBracket[ind], 10)
      }
      return players
    },
    lotteryActive() {
      const timeLeft = this.lottery !== null ? this.lottery.endTime - Date.now() : 0
      return timeLeft > 0
    }
  },
  async mounted() {
    await this.loadCurrentLottery()
    this.loadPreviousLottery()
    this.filter.lid = this.lottery.id
    if (this.user !== null) {
      this.filter.uid = this.user.address
      this.fetchTickets(this.filter)
    }
  },
  methods: {
    ...mapActions('modal', [
      'changeModalGlobalState',
      'changeModalListState',
      'changeModalWalletState'
    ]),
    ...mapActions('lottery', [
      'fetchLotteries',
      'fetchTickets',
      'addTicket'
    ]),
    openConnectModal() {
      this.changeModalWalletState({
        state: true
      })
    },
    openBuyModal() {
      this.changeModalGlobalState({
        state: true
      })
    },
    scrollToResults() {
      if (this.$refs.resultsSection !== undefined) {
        this.$refs.resultsSection.scrollIntoView({
          behavior: 'smooth'
        })
      }
    },
    openListModal() {
      this.listTitle = this.$t('lottery.round') + ' #' + this.lottery.id
      this.changeModalListState({
        state: true
      })
    },
    floatieToUsd(amount) {
      if (isNaN(amount)) {
        return '...'
      }
      return parseFloat(amount) * this.floatie.usd_price
    },
    wonNumber(index = null) {
      if (this.lotteryPrev !== null && this.lotteryPrev.finalNumber !== 0) {
        if (index !== null) {
          return this.lotteryPrev.finalNumber.toString().split('')[index]
        } else {
          return this.lotteryPrev.finalNumber
        }
      }
      return '0'
    },
    drawTimeLeft(type = 'days') {
      if (this.lottery.endTime !== null) {
        const timeLeft = this.lottery.endTime - Date.now()
        const munites = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
        const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const days = Math.floor(timeLeft / 1000 / 60 / 60 / 24)
        switch (type) {
          case 'days':
            return days.toString().length <= 1 ? '0' + days : days
          case 'hours':
            return hours.toString().length <= 1 ? '0' + hours : hours
          case 'minutes':
            return munites.toString().length <= 1 ? '0' + munites : munites
        }
        return days.toString().length <= 1 ? '0' + days : days
      } else {
        return '00'
      }
    },
    grabAmount(pool = 'current', index = 0) {
      if (isNaN(index)) {
        return '0'
      }
      const poolAmount = pool === 'current' ? this.lottery.amountCollectedInFloatie : this.lotteryPrev.amountCollectedInFloatie
      const withFees = poolAmount / 100 * this.matchingBreakdown[parseInt(index, 10)]
      return this.$priceFormat(withFees, 2)
    },
    getBurnAmount(pool = 'current') {
      if (this.lottery === null || this.lotteryPrev === null) {
        return '0'
      }
      const poolAmount = pool === 'current' ? this.lottery.amountCollectedInFloatie : this.lotteryPrev.amountCollectedInFloatie
      const withFees = poolAmount / 100 * 20
      return this.$priceFormat(withFees, 1)
    },
    openAccordion(index) {
      if (this.$refs['accordion' + index] !== undefined && this.$refs['accordionArea' + index] !== undefined) {
        this.accordions['accordion' + index] = !this.accordions['accordion' + index]
        if (this.accordions['accordion' + index]) {
          this.$refs['accordionArea' + index].style.maxHeight = this.$refs['accordionArea' + index].scrollHeight + 'px'
        } else {
          this.$refs['accordionArea' + index].style.maxHeight = null
        }
      }
    },
    changeModalTitle(titleType) {
      switch (titleType) {
        case 'loading':
          this.modalTitle = this.$t('lottery.waiting_for_confirmation')
          break
        case 'success':
          this.modalTitle = this.$t('lottery.success')
          break
        case 'error':
          this.modalTitle = this.$t('lottery.error_header')
          break
        default:
          this.modalTitle = this.$t('lottery.buy_tickets')
      }
    },
    closeModal() {
      this.changeModalGlobalState({
        state: false
      })
    },
    closeListModal() {
      this.changeModalListState({
        state: false
      })
    },
    parseFilledNumber(number) {
      const numberAsArray = number.split('')
      numberAsArray.reverse()
      return '1' + numberAsArray.join('')
    },
    parseRetrievedNumber(number) {
      const numberAsArray = number.split('')
      numberAsArray.splice(0, 1)
      numberAsArray.reverse()
      return numberAsArray.join('')
    },
    async buyTickets(formData) {
      try {
        const userAddress = (await window.eth.eth.requestAccounts())[0]
        const Lotteryfile = await import('~/assets/abis/Lottery.json')
        const Lottery = Lotteryfile.default
        const lotteryContract = new window.eth.eth.Contract(Lottery, this.$config.periodLottery)
        const ticketNumbers = this.generageTicketNumbers(formData.numbers)

        const gasPrice = await this.$calculateGasCost()
        const gasFeeEstimated = await lotteryContract.methods.buyTickets(this.lottery.id, ticketNumbers)
          .estimateGas({ from: userAddress, gas: this.transaction.gasFee })

        this.transaction.pending = true
        await lotteryContract.methods.buyTickets(this.lottery.id, ticketNumbers)
          .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
          .then((result) => {
            this.transaction.loading = false
            this.transaction.pending = false
            this.transaction.tx = result.transactionHash
            this.backendAddTickets(formData)
          })
          .catch((err) => {
            this.transaction.pending = false
            this.transaction.loading = false
            if (err.code === 4001) {
              if (this.$refs.buyTickets !== undefined) {
                this.$refs.buyTickets.rejectTransaction({
                  type: 'denied'
                })
              }
            } else {
              const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
              if (this.$refs.buyTickets !== undefined) {
                this.$refs.buyTickets.rejectTransaction({
                  type: 'error',
                  message: errorMessage
                })
              }
            }
          })
      } catch (err) {
        this.transaction.loading = false
        this.transaction.pending = false
        console.log(err)
      }
    },
    backendAddTickets(formData) {
      this.addTicket({
        lid: this.lottery.id,
        rolls: formData.numbers,
        price: this.lottery.priceTicketInFloatie,
        discount: this.lottery.discountDivisor,
        fee: this.lottery.treasuryFee,
        tx: this.transaction.tx
      }).then(() => {
        if (this.$refs.buyTickets !== undefined) {
          this.$refs.buyTickets.initSuccessScreen()
          this.loadCurrentLottery()
        }
      })
    },
    generageTicketNumbers(numbers) {
      const answer = []
      for (let ind = 0; ind < numbers.length; ++ind) {
        answer.push(this.parseFilledNumber(numbers[ind]))
      }
      return answer
    },
    renderTicketNumbers(numbers) {
      const answer = []
      for (let ind = 0; ind < numbers.length; ++ind) {
        answer.push(this.parseRetrievedNumber(numbers[ind]))
      }
      return answer
    },
    async loadCurrentLottery() {
      try {
        const LotteryFile = await import('~/assets/abis/Lottery.json')
        const Lottery = LotteryFile.default
        const lotteryContract = new window.eth.eth.Contract(Lottery, this.$config.periodLottery)

        const currentId = await lotteryContract.methods.viewCurrentLotteryId().call()
        const lottery = await lotteryContract.methods.viewLottery(currentId).call()
        this.lottery.id = parseInt(currentId, 10)
        this.lottery.status = parseInt(lottery.status, 10)
        this.lottery.startTime = new Date(parseInt(lottery.startTime, 10) * 1000)
        this.lottery.endTime = new Date(parseInt(lottery.endTime, 10) * 1000)
        this.lottery.priceTicketInFloatie = parseFloat(window.eth.utils.fromWei(lottery.amountCollectedInFloatie))
        this.lottery.discountDivisor = parseFloat(window.eth.utils.fromWei(lottery.discountDivisor))
        this.lottery.rewardsBreakdown = lottery.rewardsBreakdown
        this.lottery.treasuryFee = parseFloat(window.eth.utils.fromWei(lottery.treasuryFee)) / 100
        this.lottery.floatiePerBracket = lottery.floatiePerBracket
        this.lottery.countWinnersPerBracket = lottery.countWinnersPerBracket
        this.lottery.firstTicketId = parseInt(lottery.firstTicketId, 10)
        this.lottery.firstTicketIdNextLottery = parseInt(lottery.firstTicketIdNextLottery, 10)
        this.lottery.amountCollectedInFloatie = parseFloat(window.eth.utils.fromWei(lottery.amountCollectedInFloatie))
        this.lottery.finalNumber = parseInt(lottery.finalNumber, 10)
        this.lotteryLoading = false
      } catch (err) {
        console.log(err)
        this.lotteryLoading = false
      }
    },
    async loadPreviousLottery() {
      if (this.lottery.id >= 2) {
        try {
          const LotteryFile = await import('~/assets/abis/Lottery.json')
          const Lottery = LotteryFile.default
          const lotteryContract = new window.eth.eth.Contract(Lottery, this.$config.periodLottery)

          const lottery = await lotteryContract.methods.viewLottery(this.lottery.id - 1).call()
          if (this.user !== null) {
            const userTickets = await lotteryContract.methods.viewUserInfoForLotteryId(this.user.address, this.lottery.id - 1, 0, 100).call()
            this.userPrevTickets = this.renderTicketNumbers(userTickets[1])
          }
          this.lotteryPrev.id = parseInt(this.lottery.id - 1, 10)
          this.lotteryPrev.status = parseInt(lottery.status, 10)
          this.lotteryPrev.startTime = new Date(parseInt(lottery.startTime, 10) * 1000)
          this.lotteryPrev.endTime = new Date(parseInt(lottery.endTime, 10) * 1000)
          this.lotteryPrev.priceTicketInFloatie = parseFloat(window.eth.utils.fromWei(lottery.amountCollectedInFloatie))
          this.lotteryPrev.discountDivisor = parseFloat(window.eth.utils.fromWei(lottery.discountDivisor))
          this.lotteryPrev.rewardsBreakdown = lottery.rewardsBreakdown
          this.lotteryPrev.treasuryFee = parseFloat(window.eth.utils.fromWei(lottery.treasuryFee)) / 100
          this.lotteryPrev.floatiePerBracket = lottery.floatiePerBracket
          this.lotteryPrev.countWinnersPerBracket = lottery.countWinnersPerBracket
          this.lotteryPrev.firstTicketId = parseInt(lottery.firstTicketId, 10)
          this.lotteryPrev.firstTicketIdNextLottery = parseInt(lottery.firstTicketIdNextLottery, 10)
          this.lotteryPrev.amountCollectedInFloatie = parseFloat(window.eth.utils.fromWei(lottery.amountCollectedInFloatie))
          this.lotteryPrev.finalNumber = parseInt(lottery.finalNumber, 10)
          this.lotteryPrevLoading = false
        } catch (err) {
          console.log(err)
          this.lotteryPrevLoading = false
        }
      } else {
        this.lotteryPrevLoading = false
      }
    },
    changeHistoryTab(newTab) {
      if (newTab !== undefined && newTab !== null && this.history.filter !== newTab.toLowerCase()) {
        this.history.filter = newTab.toLowerCase()
      }
    }
  }
}
</script>

<style scoped>
    @import url('~/assets/css/new/polo.css');
</style>
